import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaSearchPlus, FaPlus, FaMinus, FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const designs = [
  {
    title: 'SaaS Platform Architecture',
    image: '/architecture/saas.svg',
    description:
      'Scalable SaaS architecture with tenant-aware services, asynchronous processing, and performance-focused data access patterns.',
    components: ['API Layer', 'Queue Workers', 'Redis Cache', 'Database Layer', 'Cloud Storage'],
  },
  {
    title: 'Queue Processing System',
    image: '/architecture/queues.svg',
    description:
      'Background processing pipeline for heavy workloads, retries, scheduled jobs, and resilient event-driven task handling.',
    components: ['API Layer', 'Queue Workers', 'Redis Cache', 'Database Layer', 'Cloud Storage'],
  },
  {
    title: 'API-Driven Platform Architecture',
    image: '/architecture/api.svg',
    description:
      'API-first backend design focused on modular services, secure integrations, and predictable data contracts across clients.',
    components: ['API Layer', 'Queue Workers', 'Redis Cache', 'Database Layer', 'Cloud Storage'],
  },
];

const SystemDesignV2 = () => {
  const [activeIndex, setActiveIndex] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const activeDiagram = activeIndex !== null ? designs[activeIndex] : null;

  const resetView = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setIsDragging(false);
  };

  const openDiagram = (index) => {
    setActiveIndex(index);
    resetView();
  };

  const closeDiagram = () => {
    setActiveIndex(null);
    resetView();
  };

  const zoomIn = () => setZoom((prev) => Math.min(prev + 0.25, 3));
  const zoomOut = () =>
    setZoom((prev) => {
      const next = Math.max(prev - 0.25, 0.75);
      if (next <= 1) setPan({ x: 0, y: 0 });
      return next;
    });

  const goToNext = () => {
    if (activeIndex === null) return;
    setActiveIndex((prev) => (prev + 1) % designs.length);
    resetView();
  };

  const goToPrev = () => {
    if (activeIndex === null) return;
    setActiveIndex((prev) => (prev - 1 + designs.length) % designs.length);
    resetView();
  };

  const onPointerDown = (event) => {
    if (zoom <= 1) return;
    setIsDragging(true);
    setDragStart({ x: event.clientX - pan.x, y: event.clientY - pan.y });
  };

  const onPointerMove = (event) => {
    if (!isDragging || zoom <= 1) return;
    setPan({ x: event.clientX - dragStart.x, y: event.clientY - dragStart.y });
  };

  const onPointerUp = () => {
    setIsDragging(false);
  };

  return (
    <>
      <section className="py-16 md:py-20 lg:py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="w-full rounded-2xl bg-white border border-gray-200 p-6 md:p-8">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6 tracking-tight text-gray-900">
              System Design &amp; Architecture
            </h2>
            <div className="w-16 h-1 bg-blue-600 rounded mt-2 mb-6"></div>
            <p className="text-sm md:text-base text-gray-600 max-w-2xl mb-8">
              Backend architecture patterns used to design reliable, scalable, and production-ready systems.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {designs.map((design, index) => (
                <motion.article
                  key={design.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, delay: index * 0.08 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -8 }}
                  className="rounded-xl bg-gray-50 border border-gray-200 p-6 md:p-8 shadow-md hover:shadow-lg hover:-translate-y-1 transition-all duration-300 space-y-3"
                >
                  <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-4">{design.title}</h3>

                  <div className="bg-gray-900 rounded-lg p-4 mb-4 shadow-md">
                    <button
                      type="button"
                      onClick={() => openDiagram(index)}
                      className="w-full rounded-lg overflow-hidden border border-gray-700 bg-gray-800 flex items-center justify-center relative group"
                    >
                      <img
                        src={design.image}
                        alt={`${design.title} diagram`}
                        className="w-full h-auto object-contain rounded-lg"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
                        <span className="opacity-0 group-hover:opacity-100 inline-flex items-center gap-2 bg-white text-gray-900 px-3 py-2 rounded-lg text-sm font-semibold transition-opacity duration-300">
                          <FaSearchPlus />
                          Click to Zoom
                        </span>
                      </div>
                    </button>
                  </div>

                  <p className="max-w-prose text-sm md:text-base text-gray-600 leading-relaxed mb-3">{design.description}</p>

                  <div>
                    <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-2">
                      Key Architecture Components
                    </h4>
                    <ul className="space-y-1">
                      {design.components.map((component) => (
                        <li key={component} className="text-xs text-gray-600">
                          - {component}
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <AnimatePresence>
        {activeDiagram && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm p-4 md:p-8"
          >
            <div className="max-w-7xl mx-auto h-full flex flex-col">
              <div className="flex items-center justify-between mb-4 text-white">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={goToPrev}
                    className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-white/20 hover:bg-white/30 transition"
                    aria-label="Previous diagram"
                  >
                    <FaChevronLeft />
                  </button>
                  <button
                    type="button"
                    onClick={goToNext}
                    className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-white/20 hover:bg-white/30 transition"
                    aria-label="Next diagram"
                  >
                    <FaChevronRight />
                  </button>
                  <h3 className="text-lg md:text-xl font-semibold">{activeDiagram.title}</h3>
                  <span className="text-sm text-white/70">
                    {activeIndex + 1} / {designs.length}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={zoomOut}
                    className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-white/20 hover:bg-white/30 transition"
                    aria-label="Zoom out"
                  >
                    <FaMinus />
                  </button>
                  <button
                    type="button"
                    onClick={zoomIn}
                    className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-white/20 hover:bg-white/30 transition"
                    aria-label="Zoom in"
                  >
                    <FaPlus />
                  </button>
                  <button
                    type="button"
                    onClick={closeDiagram}
                    className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-white/20 hover:bg-white/30 transition"
                    aria-label="Close"
                  >
                    <FaTimes />
                  </button>
                </div>
              </div>

              <div className="flex-1 rounded-xl bg-gray-900 border border-white/20 overflow-auto">
                <div className="min-h-full min-w-full flex items-center justify-center p-6">
                  <img
                    src={activeDiagram.image}
                    alt={`${activeDiagram.title} zoomed diagram`}
                    className={`w-full h-auto max-w-none select-none ${zoom > 1 ? (isDragging ? 'cursor-grabbing' : 'cursor-grab') : 'cursor-default'}`}
                    loading="lazy"
                    style={{
                      transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                      transformOrigin: 'center center',
                    }}
                    draggable={false}
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={onPointerUp}
                    onPointerLeave={onPointerUp}
                  />
                </div>
              </div>

              <div className="mt-4 rounded-xl bg-white/10 border border-white/20 p-4 text-white">
                <p className="text-sm md:text-base text-white/90 leading-relaxed mb-3">
                  {activeDiagram.description}
                </p>
                <div className="flex flex-wrap gap-2">
                  {activeDiagram.components.map((component) => (
                    <span
                      key={component}
                      className="px-3 py-1 rounded-full text-xs font-semibold bg-white/20"
                    >
                      {component}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default SystemDesignV2;
